import { useState, useEffect } from 'react';
import { X, Package, Loader2 } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import * as analytics from '../services/analytics';

export default function BorrowModal({ item, onClose, onSuccess }) {
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Compute duration in days (for the helper text).
  const durationDays = (() => {
    if (!startDate || !returnDate) return null;
    const s = new Date(startDate);
    const e = new Date(returnDate);
    const diff = Math.round((e - s) / 86400000);
    return diff >= 0 ? diff : null;
  })();

  useEffect(() => {
    api.get('/projects?status=active').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate) {
      toast.error('Please set when you need it');
      return;
    }
    if (!returnDate) {
      toast.error('Please set a planned return date');
      return;
    }
    if (returnDate < startDate) {
      toast.error('Return date must be on or after the start date');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/borrow-requests', {
        item_id: item.id,
        request_message: message,
        start_date: startDate,
        agreed_return_date: returnDate || null,
        project_id: projectId || null,
      });
      toast.success('Borrow request sent!');
      analytics.track('borrow_requested', { item_id: item.id, category: item.category, owner_is_friend: !!item.is_friend });
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-[#004B00] focus:ring-2 focus:ring-[#004B00]/20 outline-none font-nunito text-sm bg-white text-gray-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <h3 className="font-poppins font-semibold text-gray-900">Request to Borrow</h3>
          <button onClick={onClose} data-testid="borrow-modal-close">
            <X size={18} className="text-gray-400 hover:text-gray-700 transition-colors"/>
          </button>
        </div>

        {/* Item Preview */}
        <div className="flex items-center gap-3 px-6 py-4 bg-stone-50 mx-6 mt-4 rounded-xl">
          <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-stone-100">
            {item.photo ? (
              <img src={item.photo} alt={item.title} className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package size={22} className="text-stone-400"/>
              </div>
            )}
          </div>
          <div>
            <div className="font-poppins font-semibold text-gray-900 text-sm">{item.title}</div>
            <div className="text-xs text-gray-500 font-nunito mt-0.5">Owned by {item.owner_name}</div>
            {item.category && (
              <div className="text-xs text-gray-400 font-nunito mt-0.5">{item.category}</div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-nunito">
              Why do you need it? <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              className={`${inp} resize-none`}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Tell them what you need it for..."
              data-testid="borrow-message"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-nunito">
              When do you need it? <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className={inp}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              min={today}
              required
              data-testid="borrow-start-date"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-nunito">
              For how long? <span className="text-gray-400 font-normal">(planned return)</span> <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className={inp}
              value={returnDate}
              onChange={e => setReturnDate(e.target.value)}
              min={startDate || today}
              required
              data-testid="borrow-return-date"
            />
            {durationDays !== null && (
              <p className="text-xs text-gray-500 font-nunito mt-1.5" data-testid="borrow-duration-hint">
                Duration: <span className="font-semibold text-gray-700">{durationDays === 0 ? 'Same day' : `${durationDays} day${durationDays === 1 ? '' : 's'}`}</span>
              </p>
            )}
          </div>

          {projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-nunito">
                Link to a project <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                className={inp}
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                data-testid="borrow-project-select"
              >
                <option value="">— No project —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-stone-200 text-gray-700 py-2.5 rounded-xl font-nunito font-semibold hover:bg-gray-50 transition-all text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="borrow-submit"
              className="flex-1 bg-[#FF5500] hover:bg-[#e04b00] text-white py-2.5 rounded-xl font-opensans font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-sm active:scale-95"
            >
              {submitting && <Loader2 size={15} className="animate-spin"/>}
              {submitting ? 'Sending...' : 'Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
